// components/VendorTabs/FormBuilderV2.js
// ----------------------------------------------------------------------------
// Self-contained form builder for the V2 booking widget. Drop-in replacement
// for the "configure" view inside BookingV2Dashboard.js:
//
//   import FormBuilderV2 from './FormBuilderV2';
//   ...
//   {activeView === 'configure' && <FormBuilderV2 />}
//
// Uses the SAME endpoints as the old view (GET/PUT /api/booking-v2/vendor/config,
// GET /api/vendors/profile, PUT /api/booking-v2/vendor/notifications,
// GET /api/booking-v2/vendor/services) so nothing else changes.
//
// Improvements over the old configure view:
//  1. ALL fields visible & reorderable — defaults included. Date/Time/Requests
//     are injected for older templates so they appear and persist on save.
//  2. Title & subtitle optional — show/hide toggles (widget honours them).
//  3. Field labels optional — one universal toggle (widget honours it).
//  4. CC emails as add/remove chips instead of a comma string.
//  5. Card-based UI consistent with the Manage pages.
// Preserved from the old view: Enable Widget, Show Logo, Primary Color,
// Border Style, and the full service-catalog integration (reserved
// service_selection field, option sync from active services, preview box).

import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Briefcase } from 'lucide-react';
import '../../styles/FormBuilderV2.css';

const RESERVED_SERVICE = 'service_selection';
const CORE_FIELDS = ['customer_name', 'customer_email', 'booking_date']; // can't delete / must stay required
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Inline reorder glyphs — rendered directly so they never depend on an icon
// font/package loading correctly (the lucide arrows were showing as empty boxes).
const ChevronUp = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="18 15 12 9 6 15" />
  </svg>
);
const ChevronDown = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'email', label: 'Email' },
  { value: 'tel', label: 'Phone' },
  { value: 'textarea', label: 'Paragraph' },
  { value: 'select', label: 'Dropdown' },
  { value: 'date', label: 'Date' },
  { value: 'time', label: 'Time' },
  { value: 'checkbox', label: 'Checkbox' },
];

// Injected for templates that predate date/time/requests as builder fields.
const ENSURE_FIELDS = [
  { name: 'booking_date', label: 'Date Requested', type: 'date', required: true, default_field: true },
  { name: 'booking_time', label: 'Time Requested', type: 'time', required: false, default_field: true },
  { name: 'special_requests', label: 'Other Requests or Queries', type: 'textarea', required: false, default_field: true, placeholder: 'Anything we should know…' },
];

const serviceOptionsFrom = (services) =>
  services.filter(s => s.is_active).map(s => ({
    value: s.id.toString(),
    label: `${s.service_name}${s.price ? ` - R${s.price}` : ''}${s.duration_minutes ? ` (${s.duration_minutes}min)` : ''}`
  }));

function FormBuilderV2() {
  const [fields, setFields] = useState([]);
  const [settings, setSettings] = useState({});
  const [templateName, setTemplateName] = useState('Default Form');
  const [services, setServices] = useState([]);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [ccEmails, setCcEmails] = useState([]);
  const [ccInput, setCcInput] = useState('');
  const [ccError, setCcError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [toast, setToast] = useState(null);

  const authHeaders = useCallback(() => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
  }), []);

  // ---------- load ----------
  useEffect(() => {
    (async () => {
      try {
        const [cfgRes, profRes, svcRes] = await Promise.all([
          fetch('/api/booking-v2/vendor/config', { headers: authHeaders() }),
          fetch('/api/vendors/profile', { headers: authHeaders() }), // same source the old view used
          fetch('/api/booking-v2/vendor/services', { headers: authHeaders() })
        ]);

        let svc = [];
        if (svcRes.ok) {
          const sData = await svcRes.json();
          svc = sData.services || [];
          setServices(svc);
        }

        if (cfgRes.ok) {
          const data = await cfgRes.json();
          const tpl = data.template || {};
          let loaded = Array.isArray(tpl.form_fields) ? [...tpl.form_fields] : [];

          // 1) Inject date/time/requests for older templates (visible + reorderable).
          ENSURE_FIELDS.forEach(ef => {
            if (!loaded.some(f => f.name === ef.name)) {
              loaded.push({ ...ef, order: loaded.length + 1 });
            }
          });

          // 2) Keep the reserved service field's options in sync with the catalog
          //    (same behaviour as the old view's updateServiceDropdown).
          loaded = loaded.map(f =>
            (f.name === RESERVED_SERVICE && f.reserved_field)
              ? { ...f, options: serviceOptionsFrom(svc) }
              : f
          );

          loaded.sort((a, b) => (a.order || 0) - (b.order || 0));
          setFields(loaded);
          setSettings(tpl.widget_settings || {});
          setTemplateName(tpl.template_name || 'Default Form');
        }

        if (profRes.ok) {
          const p = await profRes.json();
          setNotifyEmail(p.booking_v2_notification_email || '');
          const cc = p.booking_v2_cc_emails;
          setCcEmails(Array.isArray(cc) ? cc.filter(Boolean)
            : (typeof cc === 'string' && cc ? cc.split(',').map(s => s.trim()).filter(Boolean) : []));
        }
      } catch (e) {
        console.error('FormBuilderV2 load error:', e);
        setMsg({ type: 'error', text: 'Could not load your form configuration.' });
      } finally {
        setLoading(false);
      }
    })();
  }, [authHeaders]);

  // ---------- field helpers ----------
  const patchField = (idx, patch) =>
    setFields(prev => prev.map((f, i) => (i === idx ? { ...f, ...patch } : f)));

  const move = (idx, dir) =>
    setFields(prev => {
      const next = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });

  const removeField = (idx) =>
    setFields(prev => prev.filter((_, i) => i !== idx));

  const addField = () =>
    setFields(prev => ([
      ...prev,
      {
        name: `custom_field_${Date.now()}`,
        label: 'New Field',
        type: 'text',
        required: false,
        default_field: false,
        placeholder: '',
        order: prev.length + 1
      }
    ]));

  // ---------- service catalog integration (ported from the old view) ----------
  const hasServiceField = fields.some(f => f.name === RESERVED_SERVICE && f.reserved_field);
  const activeServiceCount = services.filter(s => s.is_active).length;

  const addServiceDropdown = () => {
    const opts = serviceOptionsFrom(services);
    if (opts.length === 0) {
      setMsg({ type: 'error', text: 'Add active services to your catalog first, then add the service dropdown.' });
      return;
    }
    setFields(prev => ([
      ...prev,
      {
        name: RESERVED_SERVICE,        // cannot be changed
        label: 'Select Service',       // can be changed
        type: 'select',
        required: true,
        default_field: false,
        reserved_field: true,
        order: prev.length + 1,
        options: opts,
        placeholder: 'Choose a service...'
      }
    ]));
  };

  // ---------- options (custom dropdowns only) ----------
  const patchOption = (fIdx, oIdx, patch) =>
    setFields(prev => prev.map((f, i) => {
      if (i !== fIdx) return f;
      const options = (f.options || []).map((o, j) => (j === oIdx ? { ...o, ...patch } : o));
      return { ...f, options };
    }));

  const addOption = (fIdx) =>
    setFields(prev => prev.map((f, i) =>
      i === fIdx ? { ...f, options: [...(f.options || []), { value: `option_${Date.now()}`, label: 'New option' }] } : f
    ));

  const removeOption = (fIdx, oIdx) =>
    setFields(prev => prev.map((f, i) =>
      i === fIdx ? { ...f, options: (f.options || []).filter((_, j) => j !== oIdx) } : f
    ));

  // ---------- CC chips ----------
  const addCc = () => {
    const v = ccInput.trim().replace(/,$/, '');
    if (!v) return;
    if (!EMAIL_RE.test(v)) { setCcError('That doesn’t look like a valid email.'); return; }
    if (ccEmails.includes(v)) { setCcError('Already added.'); return; }
    setCcEmails(prev => [...prev, v]);
    setCcInput('');
    setCcError('');
  };

  const onCcKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addCc(); }
    else if (e.key === 'Backspace' && !ccInput && ccEmails.length) {
      setCcEmails(prev => prev.slice(0, -1));
    }
  };

  // ---------- save ----------
  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const ordered = fields.map((f, i) => ({ ...f, order: i + 1 }));

      const tplRes = await fetch('/api/booking-v2/vendor/config', {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          formFields: ordered,
          widgetSettings: settings,   // full object: preserves booking_mode, theme, approval/email toggles, show_field_labels
          templateName
        })
      });

      const notifRes = await fetch('/api/booking-v2/vendor/notifications', {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          notification_email: notifyEmail,
          cc_emails: ccEmails.join(', ')
        })
      });

      if (!tplRes.ok || !notifRes.ok) throw new Error();
      setFields(ordered);
      setMsg({ type: 'success', text: 'Saved! Your booking form is up to date.' });
      setToast('Settings saved');
      setTimeout(() => setToast(null), 4000);
    } catch (e) {
      console.error('FormBuilderV2 save error:', e);
      setMsg({ type: 'error', text: 'Could not save. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  // ---------- UI ----------
  if (loading) return <div className="fb2"><p className="fb2-loading">Loading your form…</p></div>;

  const set = (key, value) => setSettings(prev => ({ ...prev, [key]: value }));
  const setStyling = (key, value) =>
    setSettings(prev => ({ ...prev, styling: { ...(prev.styling || {}), [key]: value } }));
  const showTitle = settings.show_title !== false;
  const showSubtitle = settings.show_subtitle !== false;
  const showFieldLabels = settings.show_field_labels !== false;

  return (
    <div className="fb2">
      {toast && <div className="fb2-toast" role="status">✓ {toast}</div>}

      {msg && <div className={`fb2-msg ${msg.type}`}>{msg.text}</div>}

      {/* ---- Widget appearance (preserved from old view + new toggles) ---- */}
      <div className="fb2-card">
        <div className="fb2-card-head"><h3>Widget Appearance</h3><small>Title, subtitle and field labels are optional — switch off or leave blank to hide.</small></div>
        <div className="fb2-card-body">
          <div className="fb2-grid" style={{ marginBottom: 14 }}>
            <label className="fb2-switch">
              <input type="checkbox" checked={settings.enabled !== false}
                onChange={(e) => set('enabled', e.target.checked)} />
              Enable widget
            </label>
            <label className="fb2-switch">
              <input type="checkbox" checked={!!settings.show_logo}
                onChange={(e) => set('show_logo', e.target.checked)} />
              Show my logo
            </label>
            <label className="fb2-switch">
              <input type="checkbox" checked={showFieldLabels}
                onChange={(e) => set('show_field_labels', e.target.checked)} />
              Show field labels on the form
            </label>
          </div>

          <div className="fb2-grid">
            <div className="fb2-group">
              <label>Title</label>
              <input type="text" value={settings.title || ''} placeholder="Book Your Appointment"
                onChange={(e) => set('title', e.target.value)} disabled={!showTitle} />
              <label className="fb2-toggle">
                <input type="checkbox" checked={showTitle}
                  onChange={(e) => set('show_title', e.target.checked)} />
                Show the title on my form
              </label>
            </div>
            <div className="fb2-group">
              <label>Subtitle</label>
              <input type="text" value={settings.subtitle || ''} placeholder="Select your preferred date and service"
                onChange={(e) => set('subtitle', e.target.value)} disabled={!showSubtitle} />
              <label className="fb2-toggle">
                <input type="checkbox" checked={showSubtitle}
                  onChange={(e) => set('show_subtitle', e.target.checked)} />
                Show the subtitle on my form
              </label>
            </div>
            <div className="fb2-group">
              <label>Primary colour</label>
              <input className="fb2-color" type="color"
                value={settings.styling?.primary_color || '#e91e63'}
                onChange={(e) => setStyling('primary_color', e.target.value)} />
            </div>
            <div className="fb2-group">
              <label>Border style</label>
              <select value={settings.styling?.border_radius || '12px'}
                onChange={(e) => setStyling('border_radius', e.target.value)}>
                <option value="4px">Sharp & Modern</option>
                <option value="8px">Slightly Rounded</option>
                <option value="12px">Soft & Friendly</option>
                <option value="16px">Very Rounded</option>
              </select>
            </div>
            <div className="fb2-group">
              <label>Button text</label>
              <input type="text" value={settings.submit_text || ''} placeholder="Book Now"
                onChange={(e) => set('submit_text', e.target.value)} />
              <div className="fb2-hint">Shown on the booking button. Leave blank for the default.</div>
            </div>
            <div className="fb2-group">
              <label>Field size</label>
              <select value={settings.styling?.field_size || 'md'}
                onChange={(e) => setStyling('field_size', e.target.value)}>
                <option value="sm">Small</option>
                <option value="md">Medium</option>
                <option value="lg">Large</option>
              </select>
            </div>
            <div className="fb2-group">
              <label>Button colour</label>
              <input className="fb2-color" type="color"
                value={settings.styling?.button_color || settings.styling?.primary_color || '#e91e63'}
                onChange={(e) => setStyling('button_color', e.target.value)} />
            </div>
            <div className="fb2-group">
              <label>Button text colour</label>
              <input className="fb2-color" type="color"
                value={settings.styling?.button_text_color || '#ffffff'}
                onChange={(e) => setStyling('button_text_color', e.target.value)} />
            </div>
            <div className="fb2-group">
              <label>Button width</label>
              <select value={settings.styling?.button_width || 'full'}
                onChange={(e) => setStyling('button_width', e.target.value)}>
                <option value="full">Full width</option>
                <option value="large">Large (75%)</option>
                <option value="medium">Medium (50%)</option>
                <option value="small">Compact</option>
              </select>
              <div className="fb2-hint">The button is always centered.</div>
            </div>
          </div>
        </div>
      </div>

      {/* ---- Form fields ---- */}
      <div className="fb2-card">
        <div className="fb2-card-head">
          <h3>Form Fields</h3>
          <small>Use the arrows to reorder — the order here is the order on your form.</small>
        </div>
        <div className="fb2-card-body">
          {fields.map((field, idx) => {
            const isCore = CORE_FIELDS.includes(field.name);
            const isService = field.name === RESERVED_SERVICE && field.reserved_field;
            return (
              <div key={field.name + idx}
                className={`fb2-field ${field.default_field ? 'is-default' : ''} ${isService ? 'is-reserved' : ''}`}>
               <div className="fb2-order">
                  <button type="button" className="fb2-arrow" onClick={() => move(idx, -1)} disabled={idx === 0} aria-label="Move up">▲</button>
                  <button type="button" className="fb2-arrow" onClick={() => move(idx, 1)} disabled={idx === fields.length - 1} aria-label="Move down">▼</button>
                </div>

                <div className="fb2-field-main">
                  <div>
                    <label>Label</label>
                    <input type="text" value={field.label || ''}
                      onChange={(e) => patchField(idx, { label: e.target.value })} />
                  </div>
                  <div>
                    <label>Type</label>
                    {field.default_field || isService ? (
                      <input type="text" value={(FIELD_TYPES.find(t => t.value === field.type)?.label) || field.type} disabled />
                    ) : (
                      <select value={field.type}
                        onChange={(e) => patchField(idx, { type: e.target.value, ...(e.target.value === 'select' && !field.options ? { options: [] } : {}) })}>
                        {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    )}
                  </div>
                  <div>
                    <label>Placeholder</label>
                    <input type="text" value={field.placeholder || ''}
                      onChange={(e) => patchField(idx, { placeholder: e.target.value })} />
                  </div>

                  {isService && (
                    <div className="fb2-service-note">
                      🎯 Maps to your service catalogue automatically — {field.options?.length || 0} active services linked.
                      Options refresh from your catalogue; edit them under Services.
                    </div>
                  )}

                  {field.type === 'select' && !isService && (
                    <div className="fb2-options">
                      <label>Options</label>
                      {(field.options || []).map((opt, oIdx) => (
                        <div key={oIdx} className="fb2-option-row">
                          <input type="text" value={opt.label}
                            onChange={(e) => patchOption(idx, oIdx, { label: e.target.value, value: e.target.value.toLowerCase().replace(/\s+/g, '_') })} />
                          <button className="fb2-remove" onClick={() => removeOption(idx, oIdx)}>Remove</button>
                        </div>
                      ))}
                      <button className="fb2-add-option" onClick={() => addOption(idx)}>+ Add option</button>
                    </div>
                  )}
                </div>

                <div className="fb2-field-side">
                  {field.default_field && <span className="fb2-badge">Default</span>}
                  {isService && <span className="fb2-badge system">System Field</span>}
                  <label className="fb2-required">
                    <input type="checkbox" checked={!!field.required} disabled={isCore}
                      onChange={(e) => patchField(idx, { required: e.target.checked })} />
                    Required
                  </label>
                  {!isCore && (
                    <button className="fb2-remove" onClick={() => removeField(idx)}>
                      <Trash2 size={13} /> Remove
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          <div className="fb2-add-row">
            <button className="fb2-add-btn" onClick={addField}>
              <Plus size={14} /> Add custom field
            </button>
            <button className="fb2-add-btn" onClick={addServiceDropdown}
              disabled={hasServiceField || activeServiceCount === 0}
              title={hasServiceField ? 'Service selection field already added'
                : activeServiceCount === 0 ? 'Add active services to your catalogue first'
                : 'Add a service dropdown from your catalogue'}>
              <Briefcase size={14} />
              {hasServiceField ? 'Service field added' : 'Add service selection'}
            </button>
            <span className="fb2-service-meta">
              {activeServiceCount > 0
                ? `${activeServiceCount} active service${activeServiceCount === 1 ? '' : 's'} in your catalogue`
                : 'No active services yet — add them under Services'}
            </span>
          </div>
        </div>
      </div>

      {/* ---- Notifications ---- */}
      <div className="fb2-card">
        <div className="fb2-card-head"><h3>📧 Email Notifications</h3><small>Who hears about new bookings.</small></div>
        <div className="fb2-card-body">
          <div className="fb2-group" style={{ marginBottom: 16 }}>
            <label>Primary notification email</label>
            <input type="email" value={notifyEmail} placeholder="you@business.com"
              onChange={(e) => setNotifyEmail(e.target.value)} />
          </div>
          <div className="fb2-group">
            <label>CC these addresses</label>
            <div className="fb2-chips">
              {ccEmails.map(email => (
                <span key={email} className="fb2-chip">
                  {email}
                  <button onClick={() => setCcEmails(prev => prev.filter(x => x !== email))} aria-label={`Remove ${email}`}>×</button>
                </span>
              ))}
              <input className="fb2-chip-input" type="text" value={ccInput}
                placeholder={ccEmails.length ? 'Add another…' : 'Type an email and press Enter'}
                onChange={(e) => { setCcInput(e.target.value); setCcError(''); }}
                onKeyDown={onCcKeyDown} onBlur={addCc} />
              <button className="fb2-chip-add" onClick={addCc} type="button">Add</button>
            </div>
            {ccError && <div className="fb2-cc-err">{ccError}</div>}
            <div className="fb2-hint">Press Enter (or tap Add) after each address. Backspace removes the last one.</div>
          </div>
        </div>
      </div>

      <div className="fb2-savebar">
        <button className="fb2-save" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save Form & Notifications'}
        </button>
      </div>
    </div>
  );
}

export default FormBuilderV2;